<template>
  <div class="timelineWrapper">
    <div
      class="timeline"
      ref="timeline"
      :style="`height: ${Math.max(24, layers.length * 24)}px`"
    >
      <div
        class="current-time-marker"
        ref="currentTimeMarker"
        :style="`height: ${Math.max(40, layers.length * 24 + 16)}px`"
        @mousedown.prevent="isDraggingCurrentTimeMarker = true"
        @touchstart.prevent="isDraggingCurrentTimeMarker = true"
        @touchmove.prevent="handleTouchMove"
        @touchend.prevent="handleTouchEnd"
      >
        <div class="current-time-label">
          <small>{{ secondsToTime(currentTime) }}</small>
        </div>
      </div>
      <div v-for="layer in layers" :key="layer.id">
        <div class="layer">
          <div
            class="crop-handle left"
            :id="`cropHandleLeft-${layer.id}`"
            @mousedown.prevent="draggingCropHandleLeftLayerId = layer.id"
            @touchstart.prevent="
              draggingCropHandleLeftLayerId = layer.id
            "
            @touchmove.prevent="handleTouchMove"
            @touchend.prevent="handleTouchEnd"
          >
            <v-icon dark small>mdi-chevron-left</v-icon>
          </div>
          <div
            class="crop-handle right"
            :id="`cropHandleRight-${layer.id}`"
            @mousedown.prevent="
              draggingCropHandleRightLayerId = layer.id
            "
            @touchstart.prevent="
              draggingCropHandleRightLayerId = layer.id
            "
            @touchmove.prevent="handleTouchMove"
            @touchend.prevent="handleTouchEnd"
          >
            <v-icon dark small>mdi-chevron-right</v-icon>
          </div>
          <div
            class="timeline-highlight"
            :id="`timelineHighlight-${layer.id}`"
            @click="$emit('select-layer-id', layer.id)"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { convertSeconds } from "@/util/helpers";

export default {
  name: "TimelineComponent",
  props: [
    "duration",
    "currentTime",
    "isPlaying",
    "suggestedShortDuration",
    "layers",
    "selectedLayerId"
  ],
  data() {
    return {
      currentTimeMarkerPosition: 0,
      isDraggingCurrentTimeMarker: false,
      cropHandlesLeftPositions: {},
      cropHandlesRightPositions: {},
      draggingCropHandleLeftLayerId: null,
      draggingCropHandleRightLayerId: null,
      firstSeekEmitted: false,
      windowLastWidth: null,
      // colors: [
      //   "#0169EE",
      //   "#FF9C00",
      //   "#C8D746",
      //   "#00BFFF",
      //   "#FFC0CB",
      //   "#FA8072",
      //   "#32CD32",
      //   "#FFD700",
      //   "#F25649",
      //   "#9400D3",
      //   "#FF1493",
      //   "#4682B4",
      //   "#008080",
      //   "#9897BF",
      //   "#00FA9A",
      // ],
    };
  },
  mounted() {
    this.initTimeline();
  },
  methods: {
    secondsToTime(seconds) {
      return convertSeconds(seconds, true, true);
    },
    initTimeline(duration = this.duration) {
      const layer = this.layers[0];
      this.cropHandlesRightPositions[layer.id] =
        this.getCropHandleRightPositionAtSuggestedDuration(duration);
      this.$emit("set-layer-end", {
        layerId: layer.id,
        end: this.suggestedShortDuration,
      });
      this.updateTimeline();

      window.addEventListener("mousemove", this.handleMouseMove);
      window.addEventListener("mouseup", this.handleMouseUp);
    },
    getCropHandleRightPositionAtSuggestedDuration(duration = this.duration) {
      const suggestedDuration = parseFloat(this.suggestedShortDuration);
      const timelineWidth = this.timelineWidth();
      return Math.min(
        (timelineWidth / duration) * suggestedDuration,
        timelineWidth
      );
    },
    updateTimeline() {
      const currentTimeMarker = this.$refs.currentTimeMarker;
      currentTimeMarker.style.left = `${
        (this.currentTime / this.duration) * this.timelineWidth()
      }px`;

      this.layers.forEach((layer) => {
        const layerColor = this.selectedLayerId === layer.id ? "#0169EEFF" : "#0169EE52"
        const start = layer.enableBetween[0].start;
        const end = layer.enableBetween[0].end;

        const cropHandleLeft = document.getElementById(
          `cropHandleLeft-${layer.id}`
        );
        const cropHandleRight = document.getElementById(
          `cropHandleRight-${layer.id}`
        );
        const timelineHighlight = document.getElementById(
          `timelineHighlight-${layer.id}`
        );

        let cropHandleLeftPosition = this.cropHandlesLeftPositions[layer.id];
        let cropHandleRightPosition =
          this.cropHandlesRightPositions[layer.id];

        if (cropHandleLeftPosition === undefined) {
          cropHandleLeftPosition = this.secondsToPosition(start);
          this.cropHandlesLeftPositions[layer.id] = cropHandleLeftPosition;
        }
        if (cropHandleRightPosition === undefined) {
          cropHandleRightPosition = this.secondsToPosition(end);
          this.cropHandlesRightPositions[layer.id] = cropHandleRightPosition;
        }

        cropHandleLeft.style.left = `${cropHandleLeftPosition - 24}px`;
        cropHandleLeft.style.backgroundColor = layerColor;
        cropHandleRight.style.left = `${cropHandleRightPosition}px`;
        cropHandleRight.style.backgroundColor = layerColor;
        timelineHighlight.style.left = `${cropHandleLeftPosition}px`;
        timelineHighlight.style.right = `${
          this.timelineWidth() - cropHandleRightPosition
        }px`;
        timelineHighlight.style.borderTop = `2px solid ${layerColor}`;
        timelineHighlight.style.borderBottom = `2px solid ${layerColor}`;
      });
    },
    timelineWidth() {
      try {
        const timeline = this.$refs.timeline;
        return timeline.clientWidth;
      } catch (error) {
        return 0;
      }
    },
    timelineOffsetLeft() {
      const timeline = this.$refs.timeline;
      return timeline.getBoundingClientRect().left;
    },
    positionToSeconds(position) {
      return (position / this.timelineWidth()) * this.duration;
    },
    secondsToPosition(seconds) {
      const s = parseFloat(seconds);
      const timelineWidth = this.timelineWidth();
      return Math.min((timelineWidth / this.duration) * s, timelineWidth);
    },
    handleMouseMove(event) {
      if (this.isDraggingCurrentTimeMarker)
        this.currentTimeMarkerPosition = Math.max(
          0,
          event.clientX - this.timelineOffsetLeft()
        );
      else if (this.draggingCropHandleLeftLayerId) {
        this.cropHandlesLeftPositions[this.draggingCropHandleLeftLayerId] =
          Math.max(
            0,
            Math.min(
              this.cropHandlesRightPositions[
                this.draggingCropHandleLeftLayerId
              ] || 0,
              event.clientX - this.timelineOffsetLeft()
            )
          );
        this.updateTimeline();
        const currentTime = this.positionToSeconds(
          this.cropHandlesLeftPositions[this.draggingCropHandleLeftLayerId]
        );
        this.$emit("seek", currentTime);
        this.$emit("set-layer-start", {
          layerId: this.draggingCropHandleLeftLayerId,
          start: currentTime,
        });
      } else if (this.draggingCropHandleRightLayerId) {
        this.cropHandlesRightPositions[this.draggingCropHandleRightLayerId] =
          Math.max(
            this.cropHandlesLeftPositions[this.draggingCropHandleRightLayerId] ||
              0,
            Math.min(
              this.timelineWidth(),
              event.clientX - this.timelineOffsetLeft()
            )
          );
        this.updateTimeline();
        const currentTime = this.positionToSeconds(
          this.cropHandlesRightPositions[this.draggingCropHandleRightLayerId]
        );
        this.$emit("seek", currentTime);
        this.$emit("set-layer-end", {
          layerId: this.draggingCropHandleRightLayerId,
          end: currentTime,
        });
      }
    },
    handleMouseUp() {
      this.isDraggingCurrentTimeMarker = false;
      this.draggingCropHandleLeftLayerId = null;
      this.draggingCropHandleRightLayerId = null;
    },
    handleTouchMove(event) {
      const touch = event.touches[0];
      if (this.isDraggingCurrentTimeMarker)
        this.currentTimeMarkerPosition = Math.max(
          0,
          touch.clientX - this.timelineOffsetLeft()
        );
      else if (this.draggingCropHandleLeftLayerId) {
        this.cropHandlesLeftPositions[this.draggingCropHandleLeftLayerId] =
          Math.max(
            0,
            Math.min(
              this.cropHandlesRightPositions[
                this.draggingCropHandleLeftLayerId
              ] || 0,
              touch.clientX - this.timelineOffsetLeft()
            )
          );
        this.updateTimeline();
        const currentTime = this.positionToSeconds(
          this.cropHandlesLeftPositions[this.draggingCropHandleLeftLayerId]
        );
        this.$emit("seek", currentTime);
        this.$emit("set-layer-start", {
          layerId: this.draggingCropHandleLeftLayerId,
          start: currentTime,
        });
      } else if (this.draggingCropHandleRightLayerId) {
        this.cropHandlesRightPositions[this.draggingCropHandleRightLayerId] =
          Math.max(
            this.cropHandlesLeftPositions[this.draggingCropHandleRightLayerId] ||
              0,
            Math.min(
              this.timelineWidth(),
              touch.clientX - this.timelineOffsetLeft()
            )
          );
        this.updateTimeline();
        const currentTime = this.positionToSeconds(
          this.cropHandlesRightPositions[this.draggingCropHandleRightLayerId]
        );
        this.$emit("seek", currentTime);
        this.$emit("set-layer-end", {
          layerId: this.draggingCropHandleRightLayerId,
          end: currentTime,
        });
      }
    },
    handleTouchEnd() {
      this.isDraggingCurrentTimeMarker = false;
      this.draggingCropHandleLeftLayerId = null;
      this.draggingCropHandleRightLayerId = null;
    },
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.handleMouseMove);
    window.removeEventListener("mouseup", this.handleMouseUp);
  },
  watch: {
    layers() {
      this.$nextTick(() => {
        this.updateTimeline();
      });
    },
    currentTime() {
      if (this.isPlaying) this.updateTimeline();
    },
    currentTimeMarkerPosition() {
      const currentTime =
        (this.currentTimeMarkerPosition / this.timelineWidth()) * this.duration;
      this.$emit("seek", currentTime);
      this.updateTimeline();
    },
  },
};
</script>

<style scoped>
.timeline {
  position: relative;
  /* height: 48px; */
  margin-left: 24px;
  margin-right: 24px;
  border-radius: 8px;
}

.theme--dark .timeline {
  background-color: rgba(38, 38, 38, 0.5);
}

.theme--light .timeline {
  background-color: var(--v-grey-base);
}

.timeline-highlight {
  position: absolute;
  height: 24px;
}

.layer {
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: column;
  flex-direction: column;
  /* margin-bottom: 24px; */
  height: 24px;
}

.current-time-marker {
  position: absolute;
  left: 0;
  width: 1px;
  /* height: 64px; */
  top: 0;
  background-color: var(--v-primary-base);
}

.current-time-label {
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  background-color: var(--v-primary-base);
  padding: 2px 4px;
  border-radius: 4px;
}

.crop-handle {
  /* z-index: 99; */
  position: absolute;
  display: flex;
  height: 24px;
  width: 24px;
  cursor: ew-resize;
  align-items: center;
  -webkit-justify-content: center;
  justify-content: center;
  color: black;
  border-radius: 6px;
}

.crop-handle.left {
  left: -24px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.crop-handle.right {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>